import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import SubscriptionFilter from "@/components/SubscriptionFilter";
import {
  ShieldCheck,
  Skull,
  Server,
  AlertTriangle,
  RefreshCw,
  Brain,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Zap,
  Trash2,
  Scale,
  Tag,
  Loader2,
  ShieldAlert,
  CheckCircle2,
  XCircle,
  Lightbulb,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

function HealthGauge({ score, label: customLabel }) {
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 80 ? "var(--hig-accent)" : score >= 60 ? "#fbbf24" : "var(--hig-danger)";
  const label = customLabel || (score >= 80 ? "Healthy" : score >= 60 ? "Needs Attention" : "Critical");
  return (
    <div className="flex flex-col items-center gap-2" data-testid="health-gauge">
      <div className="relative w-32 h-32">
        <svg className="w-32 h-32 -rotate-90" viewBox="0 0 128 128">
          <circle cx="64" cy="64" r={radius} fill="none" stroke="var(--n-border-2)" strokeWidth="10" />
          <circle cx="64" cy="64" r={radius} fill="none" stroke={color} strokeWidth="10" strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" className="transition-all duration-1000" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold font-mono" style={{ color }}>{score}</span>
        </div>
      </div>
      <span className="text-xs font-medium" style={{ color }}>{label}</span>
    </div>
  );
}

const SEVERITY_COLORS = {
  critical: { bg: "bg-red-500/10", text: "text-red-600 dark:text-red-400", border: "border-red-500/20", badge: "bg-red-500/20 text-red-600 dark:text-red-400" },
  high: { bg: "bg-orange-500/10", text: "text-orange-600 dark:text-orange-400", border: "border-orange-500/20", badge: "bg-orange-500/20 text-orange-600 dark:text-orange-400" },
  medium: { bg: "bg-amber-500/10", text: "text-amber-600 dark:text-amber-400", border: "border-amber-500/20", badge: "bg-amber-500/20 text-amber-600 dark:text-amber-400" },
  low: { bg: "bg-[var(--hig-accent)]/10", text: "text-[var(--hig-accent)]", border: "border-[var(--hig-accent)]/20", badge: "bg-[var(--hig-accent)]/20 text-[var(--hig-accent)]" },
};

const PRIORITY_STYLES = {
  P0: "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20",
  P1: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20",
  P2: "bg-[var(--hig-accent)]/10 text-[var(--hig-accent)] border-[var(--hig-accent)]/20",
};

const CATEGORY_ICONS = {
  security: ShieldAlert,
  compliance: Scale,
  cost: Zap,
  tagging: Tag,
  architecture: Server,
};

function FindingCard({ item, type }) {
  const [open, setOpen] = useState(false);
  const sev = SEVERITY_COLORS[item.severity] || SEVERITY_COLORS.medium;
  return (
    <div className={`border ${sev.border} rounded-lg overflow-hidden`} data-testid={`finding-${type}`}>
      <button className="w-full flex items-center gap-3 p-3 text-left hover:bg-[var(--n-hover)] transition-colors" onClick={() => setOpen(!open)}>
        <div className={`p-1.5 rounded ${sev.bg} shrink-0`}>
          {type === "zombie" ? <Skull className={`w-3.5 h-3.5 ${sev.text}`} /> : type === "overprov" ? <Server className={`w-3.5 h-3.5 ${sev.text}`} /> : <AlertTriangle className={`w-3.5 h-3.5 ${sev.text}`} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium truncate">{item.name || item.title}</span>
            <Badge className={`text-[10px] ${sev.badge} border-0 shrink-0`}>{item.severity}</Badge>
          </div>
          <p className="text-[10px] text-muted-foreground truncate mt-0.5">{item.resource_group || item.description}</p>
        </div>
        {type === "zombie" && item.estimated_monthly_waste > 0 && (
          <span className="text-xs font-mono text-red-600 dark:text-red-400 shrink-0">${item.estimated_monthly_waste}</span>
        )}
        {open ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground shrink-0" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
      </button>
      {open && (
        <div className="px-3 pb-3 pt-0 border-t border-[var(--n-border-2)] space-y-1.5">
          {item.reason && <p className="text-[10px] text-muted-foreground"><span className="text-foreground font-medium">Reason:</span> {item.reason}</p>}
          {item.issue && <p className="text-[10px] text-muted-foreground"><span className="text-foreground font-medium">Issue:</span> {item.issue}</p>}
          {item.recommendation && <p className="text-[10px] text-muted-foreground"><span className="text-foreground font-medium">Fix:</span> {item.recommendation}</p>}
          {item.type && <p className="text-[10px] text-muted-foreground"><span className="text-foreground font-medium">Type:</span> {item.type}</p>}
          {item.potential_savings_pct > 0 && <p className="text-[10px] text-emerald-600 dark:text-emerald-400">Potential savings: ~{item.potential_savings_pct}%</p>}
          {item.affected_resources?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {item.affected_resources.slice(0, 6).map((r, i) => (
                <Badge key={i} variant="outline" className="text-[10px] border-[var(--n-border-2)]">{r}</Badge>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function GovernanceFinding({ finding }) {
  const [open, setOpen] = useState(false);
  const CatIcon = CATEGORY_ICONS[finding.category] || AlertTriangle;
  const prioStyle = PRIORITY_STYLES[finding.priority] || PRIORITY_STYLES.P2;
  return (
    <div className={`border ${prioStyle.split(" ")[2]} rounded-lg overflow-hidden`} data-testid={`gov-finding-${finding.title}`}>
      <button className="w-full flex items-center gap-3 p-3 text-left hover:bg-[var(--n-hover)] transition-colors" onClick={() => setOpen(!open)}>
        <Badge className={`text-[10px] ${prioStyle} border shrink-0`}>{finding.priority}</Badge>
        <CatIcon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="text-xs font-medium truncate block">{finding.title}</span>
        </div>
        {finding.affected_count > 0 && (
          <span className="text-[10px] font-mono text-muted-foreground shrink-0">{finding.affected_count} affected</span>
        )}
        {open ? <ChevronUp className="w-3 h-3 text-muted-foreground" /> : <ChevronDown className="w-3 h-3 text-muted-foreground" />}
      </button>
      {open && (
        <div className="px-3 pb-3 pt-0 border-t border-[var(--n-border)] space-y-1.5">
          <p className="text-[11px] text-muted-foreground">{finding.description}</p>
          {finding.recommendation && (
            <p className="text-[11px] text-emerald-500"><span className="font-medium">Fix:</span> {finding.recommendation}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function HygienePage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [subscriptionId, setSubscriptionId] = useState("");
  const [governance, setGovernance] = useState(null);
  const [govLoading, setGovLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("hygiene");

  const fetchHistory = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (subscriptionId) params.append("subscription_id", subscriptionId);
      const res = await api().get(`/hygiene/reports?limit=5&${params}`);
      setHistory(res.data);
      if (res.data.length > 0 && !report) {
        setReport(res.data[0]);
      }
    } catch {}
  }, [api, subscriptionId, report]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const runAnalysis = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (subscriptionId) params.append("subscription_id", subscriptionId);
      const res = await api().post(`/hygiene/analyze?${params}`);
      setReport(res.data);
      toast.success("Hygiene analysis complete");
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Analysis failed");
    } finally {
      setLoading(false);
    }
  };

  const runGovernance = async () => {
    setGovLoading(true);
    try {
      const params = new URLSearchParams();
      if (subscriptionId) params.append("subscription_id", subscriptionId);
      const res = await api().get(`/hygiene/governance?${params}`);
      setGovernance(res.data);
      toast.success("Governance analysis complete");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Governance analysis failed");
    } finally {
      setGovLoading(false);
    }
  };

  const zombies = report?.zombies || [];
  const overProv = report?.over_provisioned || [];
  const anomalies = report?.anomalies || [];
  const recs = report?.recommendations || [];
  const ai = governance?.ai_analysis;

  return (
    <div className="space-y-8" data-testid="hygiene-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-heading">Cloud Hygiene & Governance</h1>
          <p className="text-sm text-muted-foreground mt-1">AI-powered infrastructure health, policy compliance & security posture</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SubscriptionFilter value={subscriptionId} onChange={setSubscriptionId} />
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[var(--n-surface)] border border-[var(--n-border)]">
          <TabsTrigger value="hygiene" className="data-[state=active]:bg-[var(--hig-accent)]/10 data-[state=active]:text-[var(--hig-accent)]" data-testid="tab-hygiene">
            <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Hygiene
          </TabsTrigger>
          <TabsTrigger value="governance" className="data-[state=active]:bg-violet-500/10 data-[state=active]:text-violet-500" data-testid="tab-governance">
            <Scale className="w-3.5 h-3.5 mr-1.5" /> Governance
          </TabsTrigger>
        </TabsList>

        {/* Hygiene Tab */}
        <TabsContent value="hygiene" className="space-y-6 mt-6">
          <div className="flex justify-end">
            <Button onClick={runAnalysis} disabled={loading} size="sm" className="bg-[var(--hig-accent)] hover:opacity-90" data-testid="run-hygiene-btn">
              <Brain className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-pulse" : ""}`} />
              {loading ? "Analyzing..." : "Run Analysis"}
            </Button>
          </div>

          {!report && !loading && (
            <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
              <CardContent className="py-16 text-center">
                <ShieldCheck className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm font-medium">No hygiene report yet</p>
                <p className="text-xs text-muted-foreground mt-1 mb-4">Run an analysis to detect zombie resources, over-provisioning, and architectural anomalies.</p>
                <Button onClick={runAnalysis} disabled={loading} size="sm" className="bg-[var(--hig-accent)] hover:opacity-90" data-testid="run-hygiene-empty-btn">
                  <Brain className="w-3.5 h-3.5 mr-1.5" /> Run Cloud Hygiene Analysis
                </Button>
              </CardContent>
            </Card>
          )}

          {loading && (
            <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
              <CardContent className="py-12 text-center">
                <RefreshCw className="w-8 h-8 text-[var(--hig-accent)] mx-auto mb-3 animate-spin" />
                <p className="text-sm font-medium">Analyzing infrastructure...</p>
                <p className="text-xs text-muted-foreground mt-1">AI is scanning for zombies, over-provisioned assets, and anomalies</p>
              </CardContent>
            </Card>
          )}

          {report && !loading && (
            <>
              {/* Summary Row */}
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                <Card className="bg-[var(--n-card)] border-[var(--n-border-2)] col-span-2 lg:col-span-1">
                  <CardContent className="p-4 flex items-center justify-center">
                    <HealthGauge score={report.health_score || 0} />
                  </CardContent>
                </Card>
                <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
                  <CardContent className="p-4">
                    <div className="p-2 rounded-lg bg-red-500/10 w-fit"><Skull className="w-4 h-4 text-red-600 dark:text-red-400" /></div>
                    <p className="text-xl font-bold font-mono mt-3">{zombies.length}</p>
                    <p className="text-[10px] text-muted-foreground">Zombie Resources</p>
                  </CardContent>
                </Card>
                <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
                  <CardContent className="p-4">
                    <div className="p-2 rounded-lg bg-amber-500/10 w-fit"><Server className="w-4 h-4 text-amber-600 dark:text-amber-400" /></div>
                    <p className="text-xl font-bold font-mono mt-3">{overProv.length}</p>
                    <p className="text-[10px] text-muted-foreground">Over-Provisioned</p>
                  </CardContent>
                </Card>
                <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
                  <CardContent className="p-4">
                    <div className="p-2 rounded-lg bg-violet-500/10 w-fit"><AlertTriangle className="w-4 h-4 text-violet-600 dark:text-violet-400" /></div>
                    <p className="text-xl font-bold font-mono mt-3">{anomalies.length}</p>
                    <p className="text-[10px] text-muted-foreground">Anomalies</p>
                  </CardContent>
                </Card>
              </div>

              {/* Executive Summary */}
              {report.summary && (
                <Card className="bg-[var(--hig-accent)]/[0.03] border-[var(--hig-accent)]/20" data-testid="hygiene-summary">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-2">
                      <Brain className="w-4 h-4 text-[var(--hig-accent)] shrink-0 mt-0.5" />
                      <p className="text-xs text-muted-foreground leading-relaxed">{report.summary}</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Findings Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Skull className="w-4 h-4 text-red-600 dark:text-red-400" /> Zombie Resources
                      {zombies.length > 0 && <Badge className="bg-red-500/20 text-red-600 dark:text-red-400 border-0 text-[10px] ml-auto">{zombies.length}</Badge>}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {zombies.length > 0 ? (
                      <div className="space-y-2 max-h-[350px] overflow-y-auto pr-1">
                        {zombies.map((z, i) => <FindingCard key={i} item={z} type="zombie" />)}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground text-center py-6">No zombie resources detected</p>
                    )}
                  </CardContent>
                </Card>
                <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Server className="w-4 h-4 text-amber-600 dark:text-amber-400" /> Over-Provisioned
                      {overProv.length > 0 && <Badge className="bg-amber-500/20 text-amber-600 dark:text-amber-400 border-0 text-[10px] ml-auto">{overProv.length}</Badge>}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {overProv.length > 0 ? (
                      <div className="space-y-2 max-h-[350px] overflow-y-auto pr-1">
                        {overProv.map((o, i) => <FindingCard key={i} item={o} type="overprov" />)}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground text-center py-6">No over-provisioned assets detected</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {anomalies.length > 0 && (
                <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-violet-600 dark:text-violet-400" /> Architectural Anomalies
                      <Badge className="bg-violet-500/20 text-violet-600 dark:text-violet-400 border-0 text-[10px] ml-auto">{anomalies.length}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                      {anomalies.map((a, i) => <FindingCard key={i} item={a} type="anomaly" />)}
                    </div>
                  </CardContent>
                </Card>
              )}

              {recs.length > 0 && (
                <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Zap className="w-4 h-4 text-emerald-600 dark:text-emerald-400" /> Prioritized Recommendations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {recs.sort((a, b) => a.priority - b.priority).map((r, i) => (
                        <div key={i} className="flex items-start gap-3 p-2 rounded-lg bg-[var(--n-hover)]" data-testid={`rec-${i}`}>
                          <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0 mt-0.5">
                            <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400">{r.priority}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium">{r.action}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-[10px] border-[var(--n-border-2)]">Impact: {r.impact}</Badge>
                              <Badge variant="outline" className="text-[10px] border-[var(--n-border-2)]">Effort: {r.effort}</Badge>
                              {r.estimated_savings && <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-mono">{r.estimated_savings}</span>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card className="bg-amber-500/[0.03] border-amber-500/20">
                <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-amber-500/10 shrink-0"><Trash2 className="w-4 h-4 text-amber-600 dark:text-amber-400" /></div>
                    <div>
                      <p className="text-sm font-medium">Take action on findings</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Use the Cleanup Wizard to generate scripts for removing zombie and orphaned resources.</p>
                    </div>
                  </div>
                  <Button onClick={() => navigate("/cleanup")} size="sm" className="bg-amber-500 hover:bg-amber-600 text-primary-foreground font-semibold shrink-0" data-testid="hygiene-cleanup-btn">
                    <ArrowRight className="w-3.5 h-3.5 mr-1.5" /> Cleanup Wizard
                  </Button>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Governance Tab */}
        <TabsContent value="governance" className="space-y-4 mt-4">
          <div className="flex justify-end">
            <Button onClick={runGovernance} disabled={govLoading} size="sm" className="bg-violet-600 hover:bg-violet-700" data-testid="run-governance-btn">
              {govLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Scale className="w-3.5 h-3.5 mr-1.5" />}
              {govLoading ? "Analyzing..." : "Run Governance Scan"}
            </Button>
          </div>

          {!governance && !govLoading && (
            <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
              <CardContent className="py-16 text-center">
                <Scale className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm font-medium">No governance report yet</p>
                <p className="text-xs text-muted-foreground mt-1 mb-4">Run a governance scan to analyze Azure Policy compliance, tag coverage, and security posture.</p>
                <Button onClick={runGovernance} disabled={govLoading} size="sm" className="bg-violet-600 hover:bg-violet-700" data-testid="run-governance-empty-btn">
                  <Scale className="w-3.5 h-3.5 mr-1.5" /> Run Governance Scan
                </Button>
              </CardContent>
            </Card>
          )}

          {govLoading && (
            <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
              <CardContent className="py-12 text-center">
                <Loader2 className="w-8 h-8 text-violet-600 dark:text-violet-400 mx-auto mb-3 animate-spin" />
                <p className="text-sm font-medium">Scanning governance posture...</p>
                <p className="text-xs text-muted-foreground mt-1">Checking policies, tags, and security configuration</p>
              </CardContent>
            </Card>
          )}

          {governance && !govLoading && (
            <>
              {/* Governance Score + Summary */}
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                {ai && (
                  <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
                    <CardContent className="p-4 flex items-center justify-center">
                      <HealthGauge score={ai.governance_score || 0} label="Governance" />
                    </CardContent>
                  </Card>
                )}
                {/* Policy Compliance */}
                <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <ShieldCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                      <span className="text-xs font-medium">Policy Compliance</span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-muted-foreground flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-600 dark:text-emerald-400" /> Compliant</span>
                        <span className="text-sm font-bold font-mono text-emerald-600 dark:text-emerald-400">{governance.compliance_summary?.compliant || 0}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-muted-foreground flex items-center gap-1"><XCircle className="w-3 h-3 text-red-600 dark:text-red-400" /> Non-Compliant</span>
                        <span className="text-sm font-bold font-mono text-red-600 dark:text-red-400">{governance.compliance_summary?.non_compliant || 0}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                {/* Tag Governance */}
                <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Tag className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                      <span className="text-xs font-medium">Tag Coverage</span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-muted-foreground">Untagged</span>
                        <span className="text-sm font-bold font-mono text-amber-600 dark:text-amber-400">{governance.tag_governance?.untagged || 0} <span className="text-[10px] text-muted-foreground">({governance.tag_governance?.untagged_pct || 0}%)</span></span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-muted-foreground">No env tag</span>
                        <span className="text-sm font-mono">{governance.tag_governance?.missing_env_tag || 0}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-muted-foreground">No owner</span>
                        <span className="text-sm font-mono">{governance.tag_governance?.missing_owner_tag || 0}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                {/* Advisor */}
                <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Lightbulb className="w-4 h-4 text-[var(--hig-accent)]" />
                      <span className="text-xs font-medium">Advisor Recommendations</span>
                    </div>
                    <p className="text-xl font-bold font-mono">{governance.advisor_recommendations?.length || 0}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">From Azure Advisor</p>
                  </CardContent>
                </Card>
              </div>

              {/* AI Executive Summary */}
              {ai?.executive_summary && (
                <Card className="bg-violet-500/[0.03] border-violet-500/20" data-testid="governance-summary">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-2">
                      <Brain className="w-4 h-4 text-violet-600 dark:text-violet-400 shrink-0 mt-0.5" />
                      <p className="text-xs text-muted-foreground leading-relaxed">{ai.executive_summary}</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Security Findings */}
              {ai?.security_findings?.length > 0 && (
                <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <ShieldAlert className="w-4 h-4 text-red-600 dark:text-red-400" /> Security Findings
                      <Badge className="bg-red-500/20 text-red-600 dark:text-red-400 border-0 text-[10px] ml-auto">{ai.security_findings.length}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-[350px] overflow-y-auto pr-1">
                      {ai.security_findings.map((f, i) => <GovernanceFinding key={i} finding={f} />)}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Governance Findings */}
              {ai?.governance_findings?.length > 0 && (
                <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Scale className="w-4 h-4 text-violet-600 dark:text-violet-400" /> Governance Findings
                      <Badge className="bg-violet-500/20 text-violet-600 dark:text-violet-400 border-0 text-[10px] ml-auto">{ai.governance_findings.length}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-[350px] overflow-y-auto pr-1">
                      {ai.governance_findings.map((f, i) => <GovernanceFinding key={i} finding={f} />)}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Quick Wins */}
              {ai?.quick_wins?.length > 0 && (
                <Card className="bg-emerald-500/[0.03] border-emerald-500/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Zap className="w-4 h-4 text-emerald-600 dark:text-emerald-400" /> Quick Wins
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {ai.quick_wins.map((w, i) => (
                        <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-[var(--n-hover)]">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                          <span className="text-xs text-muted-foreground">{w}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Untagged Resources */}
              {governance.tag_governance?.untagged_resources?.length > 0 && (
                <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Tag className="w-4 h-4 text-amber-600 dark:text-amber-400" /> Untagged Resources (Top 20)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-1">
                      {governance.tag_governance.untagged_resources.map((r, i) => (
                        <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-[var(--n-hover)]">
                          <span className="text-xs font-medium flex-1 truncate">{r.name}</span>
                          <Badge variant="outline" className="text-[10px] border-[var(--n-border-2)]">{r.type}</Badge>
                          <span className="text-[10px] text-muted-foreground shrink-0">{r.rg}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
